import { useEffect, useMemo, useRef } from "react";
import { Search, X, ScanLine, Camera } from "lucide-react";
import { HitButton } from "@/components/loading/HitButton";
import { GameIcon } from "@/components/GameIcon";
import { GAME_ACCENT } from "@/components/catalog/CatalogHeader";

export const CORAL = "#FF4438";
const API = process.env.REACT_APP_BACKEND_URL;
export const imgUrl = (u) => (!u ? null : String(u).startsWith("http") || String(u).startsWith("blob:") ? u
  : String(u).startsWith("/api/") ? `${API}${u}` : `${API}/api/files/public/${u}`);
export const money = (n) => `$${Number(n || 0).toFixed(2)}`;
// Campos de precio/cantidad: texto con validación propia (sin rueda ni flechas del navegador).
export const decimal = (v) => (String(v).replace(",", ".").replace(/[^\d.]/g, "").match(/^\d{0,6}(\.\d{0,2})?/) || [""])[0];
export const integer = (v) => String(v).replace(/\D/g, "").slice(0, 6);
const rgba = (hex, a) => { const n = parseInt(hex.slice(1), 16); return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`; };

export const Mono = ({ children, className = "", tone = "rgba(255,255,255,.45)", ...p }) => (
  <div className={`sx-mono text-[9.5px] tracking-[.14em] uppercase ${className}`} style={{ color: tone }} {...p}>{children}</div>
);

export const Title = ({ a, b, className = "" }) => (
  <h1 className={`font-extrabold tracking-[-.05em] leading-[.92] text-[34px] sm:text-[40px] text-white ${className}`}>
    {a} <span className="italic font-normal text-white/60">{b}</span>
  </h1>
);

export const StepHead = ({ step, label = "Publicar" }) => (
  <div className="flex items-center justify-between gap-2 whitespace-nowrap" data-testid="pv2-step">
    <Mono tone="#FF6B5E">{label ? `${label} · ` : ""}Paso {step} de 3</Mono>
    <div className="flex gap-1">{[1, 2, 3].map((i) => <span key={i} className="h-[3px] w-4 rounded-full" style={{ background: i <= step ? CORAL : "rgba(255,255,255,.15)" }} />)}</div>
  </div>
);

export const Panel = ({ children, className = "", tone, ...p }) => (
  <div className={`rounded-[16px] border bg-[#101010] ${className}`} style={{ borderColor: tone || "rgba(255,255,255,.1)" }} {...p}>{children}</div>
);

export const Chip = ({ on, children, className = "", ...p }) => (
  <button type="button" className={`h-10 rounded-[12px] border text-[13px] font-semibold transition-colors ${on ? "text-white" : "text-white/60 border-white/10 hover:border-white/25"} ${className}`}
    style={on ? { borderColor: CORAL, background: rgba(CORAL, 0.14) } : undefined} aria-pressed={on} {...p}>{children}</button>
);

export const Pill = ({ on, children, className = "", ...p }) => (
  <button type="button" className={`h-8 px-3 rounded-full border text-xs font-semibold transition-colors ${on ? "text-white" : "text-white/60 border-white/10 hover:border-white/25"} ${className}`}
    style={on ? { borderColor: CORAL, background: rgba(CORAL, 0.14) } : undefined} {...p}>{children}</button>
);

export const BackPill = ({ children, ...p }) => (
  <button type="button" className="h-9 px-3.5 rounded-full border border-white/15 text-[12px] text-white/80 hover:text-white" {...p}>{children}</button>
);

export const Primary = ({ className = "", ...p }) => (
  <HitButton className={`h-12 rounded-full bg-[#FF4438] text-[#080808] font-bold text-[15px] inline-flex items-center justify-center gap-2 disabled:opacity-40 ${className}`} tone="dark" {...p} />
);

export const Ghost = ({ className = "", ...p }) => (
  <button type="button" className={`h-12 rounded-full border border-white/20 text-white font-bold text-[14px] leading-tight disabled:opacity-40 ${className}`} {...p} />
);

export const Field = ({ className = "", ...p }) => (
  <input className={`h-12 w-full rounded-[12px] bg-[#171717] border border-white/10 px-3.5 text-[15px] text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF4438] ${className}`} {...p} />
);

export const Stepper = ({ value, onChange, testid }) => (
  <div className="h-12 rounded-[12px] border border-white/10 flex items-center justify-between px-3" data-testid={testid}>
    <button type="button" onClick={() => onChange(Math.max(1, value - 1))} className="text-white/60 text-lg w-6" data-testid={`${testid}-minus`}>−</button>
    <span className="sx-mono text-white text-[17px]" data-testid={`${testid}-value`}>{value}</span>
    <button type="button" onClick={() => onChange(Math.min(99, value + 1))} className="text-[#FF4438] text-lg w-6" data-testid={`${testid}-plus`}>+</button>
  </div>
);

export const GamePills = ({ games, value, onChange }) => (
  <div className="flex gap-2 overflow-x-auto no-scrollbar" role="tablist" data-testid="pv2-game-pills">
    {games.map((g) => {
      const on = g.key === value; const c = GAME_ACCENT[g.key] || g.color;
      return (
        <button key={g.key} type="button" role="tab" aria-selected={on} onClick={() => onChange(g.key)} data-testid={`pv2-game-${g.key}`}
          className={`flex-none inline-flex items-center gap-1.5 h-8 pl-1 pr-3 rounded-full border text-xs font-semibold ${on ? "text-white" : "text-white/60 border-white/10 bg-[#101010]"}`}
          style={on ? { background: rgba(c, 0.15), borderColor: c, boxShadow: `0 0 16px ${rgba(c, 0.4)}` } : undefined}>
          <GameIcon game={g.key} size="sm" />{g.label}
        </button>
      );
    })}
  </div>
);

export const SearchBox = ({ value, onChange, onClear, onScan, placeholder = "Nombre, número o set…", inputRef, autoFocus, hint, testid = "pv2-search" }) => (
  <div className="h-14 rounded-[16px] border flex items-center gap-3 pl-4 pr-2 bg-[#0d0d0d]" style={{ borderColor: CORAL, boxShadow: `0 0 26px ${rgba(CORAL, 0.25)}` }}>
    <Search size={18} className="text-white/70 shrink-0" />
    <input ref={inputRef} value={value} autoFocus={autoFocus} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="flex-1 min-w-0 bg-transparent text-[16px] text-white placeholder:text-white/35 focus:outline-none" data-testid={testid} />
    {hint}
    {value && onClear && <button type="button" onClick={onClear} className="w-9 h-9 grid place-items-center text-white/60" data-testid={`${testid}-clear`} aria-label="Borrar"><X size={16} /></button>}
    {onScan && <button type="button" onClick={onScan} className="w-10 h-10 rounded-[12px] border border-white/15 grid place-items-center text-white/80" data-testid={`${testid}-scan`} aria-label="Escanear"><ScanLine size={18} /></button>}
  </div>
);

const useObjectUrls = (files) => {
  const urls = useMemo(() => files.map((f) => (typeof f === "string" ? imgUrl(f) : URL.createObjectURL(f))), [files]);
  useEffect(() => () => { urls.forEach((u) => { if (u?.startsWith("blob:")) URL.revokeObjectURL(u); }); }, [urls]);
  return urls;
};

// Fotos en mosaico (FRENTE · DORSO · Cámara · Galería). `paths` = ya subidas; `files` = File pendientes.
export const PhotoTiles = ({ paths = [], files = [], onAdd, onRemove, min = 2, labels = ["Frente", "Dorso"], slots = 4, testid = "pv2-photos", dropProps, dragging }) => {
  const cam = useRef(null); const gal = useRef(null);
  const all = [...paths, ...files];
  const urls = useObjectUrls(all);
  const pick = (e) => { const fs = Array.from(e.target.files || []); if (fs.length) onAdd(fs); e.target.value = ""; };
  const empty = Math.max(0, slots - all.length - 2);
  return (
    <div {...(dropProps || {})} className={`grid grid-cols-4 gap-2 rounded-[14px] ${dragging ? "ring-2 ring-[#FF4438]" : ""}`} data-testid={testid}>
      {urls.map((u, i) => (
        <div key={u} className="relative aspect-[4/3] rounded-[12px] overflow-hidden border border-white/10 bg-black" data-testid={`${testid}-item-${i}`}>
          <img src={u} alt="" className="w-full h-full object-cover" />
          {labels[i] && <span className="absolute left-1 bottom-1 sx-mono text-[8px] px-1 rounded bg-black/80 text-white uppercase">{labels[i]}</span>}
          <button type="button" onClick={() => onRemove(i)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/80 grid place-items-center text-white" data-testid={`${testid}-remove-${i}`} aria-label="Quitar"><X size={11} /></button>
        </div>
      ))}
      <button type="button" onClick={() => cam.current?.click()} className="aspect-[4/3] rounded-[12px] border border-dashed border-[#FF4438]/60 text-[#FF6B5E] text-[11px] font-semibold grid place-items-center" data-testid={`${testid}-camera`}>
        <span className="grid place-items-center gap-1"><Camera size={15} />{all.length < labels.length ? labels[all.length] : "Cámara"}</span>
      </button>
      <button type="button" onClick={() => gal.current?.click()} className="aspect-[4/3] rounded-[12px] border border-dashed border-white/15 text-white/45 text-[11px] grid place-items-center" data-testid={`${testid}-gallery`}>Galería</button>
      {Array.from({ length: empty }).map((_, i) => <div key={`e${i}`} className="aspect-[4/3] rounded-[12px] border border-dashed border-white/10" />)}
      <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`${testid}-camera-input`} />
      <input ref={gal} type="file" accept="image/*" multiple className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      <span className="sr-only">{all.length}/{min}</span>
    </div>
  );
};

export const PhotoCount = ({ n, min = 2, label = "Fotos reales · mín. 2" }) => (
  <div className="flex items-center justify-between mb-2">
    <Mono>{label}</Mono>
    <span className="sx-mono text-[10px]" style={{ color: n >= min ? "#34D399" : "#fbbf24" }} data-testid="pv2-photo-count">{n}/{min}{n >= min ? " ✓" : ""}</span>
  </div>
);

export const LocalOnly = ({ ctx, on, onChange, testid }) => {
  const province = ctx.shipping?.seller_province;
  const checked = on ?? !!ctx.shipping?.local_only;
  const change = (v) => { onChange(v); ctx.setLocalOnly?.(v); };
  return (
    <label className={`flex items-start gap-2 text-[12.5px] ${province ? "text-white/85" : "text-white/40"}`} data-testid={testid}>
      <input type="checkbox" checked={checked} disabled={!province} onChange={(e) => change(e.target.checked)} className="mt-0.5 accent-[#FF4438]" data-testid={`${testid}-input`} />
      <span>Solo envíos dentro de {province || "mi provincia"}
        <span className="block text-[11px] text-white/45">{province ? "Quien busque desde otra provincia no verá esta publicación" : "Agrega tu provincia en tu perfil para usarlo"}</span>
      </span>
    </label>
  );
};

export const PhotoHint = () => <div className="text-[11px] text-white/45 mt-1.5" data-testid="pv2-photo-hint">Mín. 2 · recomendadas 4: frente, reverso y detalles</div>;
